import React, { useEffect, useMemo, useState } from 'react';
import { Label } from './ui/label';
import { RequiredLabel } from './RequiredLabel';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Users } from 'lucide-react';
import { courseTypesAPI, studentsAPI, cyclesAPI } from '../lib/api';

export const YEARS_OF_STUDY = ['1st Year', '2nd Year', '3rd Year'];
export const yearsForLength = (len) => YEARS_OF_STUDY.slice(0, parseInt(len, 10) || 3);

/** Loads the course-type catalogue (with lengths) and the enrolment years present on student records. */
export const useCohortOptions = () => {
  const [courseTypes, setCourseTypes] = useState([]);
  const [years, setYears] = useState([]);
  useEffect(() => {
    courseTypesAPI.detail().then((r) => setCourseTypes(r.data || [])).catch(() => {});
    studentsAPI.enrollmentYears().then((r) => setYears(r.data || [])).catch(() => {});
  }, []);
  const lengthFor = (type) => courseTypes.find((c) => c.name === type)?.course_length || '';
  return { courseTypes, years, lengthFor };
};

/**
 * The four cohort fields as constrained dropdowns.
 * Course type drives course length; course length limits year of study.
 * Shows a live "N students match" count.
 */
export const CohortFields = ({ form, setForm, disabled = false, invalid = () => false, invalidCls = '', testIdPrefix = 'cycle' }) => {
  const { courseTypes, years, lengthFor } = useCohortOptions();
  const [preview, setPreview] = useState(null);

  const yearOptions = useMemo(() => {
    const set = new Set(years.map(String));
    if (form.year_of_enrollment) set.add(String(form.year_of_enrollment));
    return [...set].sort((a, b) => b - a);
  }, [years, form.year_of_enrollment]);

  const studyOptions = yearsForLength(form.course_length);

  const pickType = (type) => {
    const len = lengthFor(type) || form.course_length;
    const allowed = yearsForLength(len);
    setForm({ ...form, course_type: type, course_length: len, year_of_study: allowed.includes(form.year_of_study) ? form.year_of_study : allowed[allowed.length - 1] });
  };

  useEffect(() => {
    if (!form.year_of_enrollment || !form.year_of_study || !form.course_length || !form.course_type) { setPreview(null); return; }
    const t = setTimeout(() => {
      cyclesAPI.rosterPreview({
        year_of_enrollment: parseInt(form.year_of_enrollment, 10),
        year_of_study: form.year_of_study,
        course_length: form.course_length,
        course_type: form.course_type,
      }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(t);
  }, [form.year_of_enrollment, form.year_of_study, form.course_length, form.course_type]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <RequiredLabel>Course type</RequiredLabel>
          <Select value={form.course_type || ''} onValueChange={pickType} disabled={disabled}>
            <SelectTrigger data-testid={`${testIdPrefix}-course-type`} className={invalid('course_type') ? invalidCls : ''}>
              <SelectValue placeholder={courseTypes.length ? 'Select a course type' : 'Add course types under Settings → Course Types'} />
            </SelectTrigger>
            <SelectContent>
              {courseTypes.map((t) => (
                <SelectItem key={t.name} value={t.name} data-testid={`${testIdPrefix}-course-type-opt-${t.name}`}>
                  {t.name} <span className="text-gray-400 text-xs">· {t.course_length}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Course length</Label>
          <Select value={form.course_length || ''} onValueChange={(v) => setForm({ ...form, course_length: v, year_of_study: yearsForLength(v).includes(form.year_of_study) ? form.year_of_study : yearsForLength(v).slice(-1)[0] })} disabled>
            <SelectTrigger data-testid={`${testIdPrefix}-course-length`}><SelectValue placeholder="Set by course type" /></SelectTrigger>
            <SelectContent>
              {['1 Year', '2 Year', '3 Year'].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-gray-500 mt-1">Locked — comes from the course type (Settings → Course Types).</p>
        </div>
        <div>
          <RequiredLabel>Year of enrollment</RequiredLabel>
          <Select value={form.year_of_enrollment ? String(form.year_of_enrollment) : ''} onValueChange={(v) => setForm({ ...form, year_of_enrollment: parseInt(v, 10) })} disabled={disabled}>
            <SelectTrigger data-testid={`${testIdPrefix}-year-enrollment`} className={invalid('year_of_enrollment') ? invalidCls : ''}>
              <SelectValue placeholder={yearOptions.length ? 'Select the intake year' : 'No students uploaded yet'} />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => <SelectItem key={y} value={y} data-testid={`${testIdPrefix}-year-enrollment-opt-${y}`}>Class of {y}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-gray-500 mt-1">Only intake years that exist on student records.</p>
        </div>
        <div>
          <RequiredLabel>Year of study</RequiredLabel>
          <Select value={form.year_of_study || ''} onValueChange={(v) => setForm({ ...form, year_of_study: v })} disabled={disabled}>
            <SelectTrigger data-testid={`${testIdPrefix}-year-of-study`}><SelectValue placeholder="Select year" /></SelectTrigger>
            <SelectContent>
              {studyOptions.map((y) => <SelectItem key={y} value={y} data-testid={`${testIdPrefix}-year-of-study-opt-${y}`}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.course_length && <p className="text-[10px] text-gray-500 mt-1">A {form.course_length} course has {studyOptions.length} year{studyOptions.length === 1 ? '' : 's'}.</p>}
        </div>
      </div>
      {preview && (
        <div
          data-testid={`${testIdPrefix}-roster-preview`}
          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${preview.count > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
        >
          <Users className="w-3.5 h-3.5 shrink-0" />
          {preview.count > 0 ? (
            <span><b>{preview.count} student{preview.count === 1 ? '' : 's'}</b> match this cohort{preview.sample?.length ? ` — ${preview.sample.join(', ')}${preview.count > preview.sample.length ? '…' : ''}` : ''}</span>
          ) : (
            <span><b>No students match</b> this cohort yet — check the student records or the values above.</span>
          )}
        </div>
      )}
    </div>
  );
};

export default CohortFields;
